import { isFunction } from "lodash"
import { useSearchParams } from "react-router-dom"

export function useSearchParamsState<T extends {}>(
  searchParamName: string,
  defaultValue: T,
): readonly [
  searchParamsState: T,
  setSearchParamsState: (
    newState: T | ((prevState: T) => T),
    clearAllOther?: boolean,
  ) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams()

  const acquiredSearchParam = searchParams.get(searchParamName)
  let searchParamsState: T
  try {
    searchParamsState = acquiredSearchParam
      ? JSON.parse(acquiredSearchParam)
      : defaultValue
  } catch {
    searchParamsState = (acquiredSearchParam ?? defaultValue) as T
  }

  const setSearchParamsState = (
    newState: T | ((prevState: T) => T),
    clearAllOther?: boolean,
  ) => {
    let next: Record<string, string>
    const resolvedValue =
      isFunction(newState) && typeof newState === "function"
        ? newState(searchParamsState)
        : newState
    const valueToStore =
      typeof resolvedValue === "string"
        ? resolvedValue
        : JSON.stringify(resolvedValue)

    if (clearAllOther) {
      next = { [searchParamName]: valueToStore } as Record<string, string>
    } else {
      next = Object.assign(
        {},
        [...searchParams.entries()].reduce(
          (o, [key, value]) => ({ ...o, [key]: value }),
          {},
        ),
        { [searchParamName]: valueToStore },
      )
    }

    setSearchParams(next, {
      replace: true,
    })
  }

  return [searchParamsState, setSearchParamsState] as const
}
